import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { fmtMoney } from "@/lib/money";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, RefreshCw, Wrench,
  Database, Layers, Package, Coins, ChevronDown, ChevronRight,
} from "lucide-react";

const CHECK_ICON = {
  ar_reconciliation:         Coins,
  inventory_ledger_vs_stock: Package,
  reservations_vs_projects:  Layers,
  wages_payable:             Database,
};

function StatusPill({ status }) {
  if (status === "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="w-3.5 h-3.5" /> PASS
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
      <AlertTriangle className="w-3.5 h-3.5" /> DRIFT
    </span>
  );
}

function KV({ label, value, tone = "" }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{label}</span>
      <span className={`text-sm font-semibold ${tone || "text-[#0A2540] dark:text-white"}`}>{value}</span>
    </div>
  );
}

function ArTable({ rows }) {
  if (!rows?.length) return <div className="text-sm text-[#94A3B8] py-6 text-center">No drift found.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-[#64748B] border-b border-[#E2E8F0] dark:border-slate-700">
          <tr>
            <th className="text-left px-3 py-2">Invoice</th>
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-right px-3 py-2">Expected AR</th>
            <th className="text-right px-3 py-2">Ledger AR</th>
            <th className="text-right px-3 py-2">Δ</th>
            <th className="text-left px-3 py-2">Hint</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.invoice_id} className="border-b border-[#F1F5F9] dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td className="px-3 py-2 font-mono text-xs">{r.invoice_number || r.invoice_id}</td>
              <td className="px-3 py-2 truncate max-w-[220px]">{r.title || "—"}</td>
              <td className="px-3 py-2 text-right">{fmtMoney(r.amount)}</td>
              <td className="px-3 py-2 text-right">{fmtMoney(r.expected_ar)}</td>
              <td className="px-3 py-2 text-right">{fmtMoney(r.actual_ar)}</td>
              <td className={`px-3 py-2 text-right font-semibold ${r.delta < 0 ? "text-red-600" : "text-amber-600"}`}>{fmtMoney(r.delta)}</td>
              <td className="px-3 py-2 text-xs text-[#64748B]">{r.hint}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QtyTable({ rows, cols }) {
  if (!rows?.length) return <div className="text-sm text-[#94A3B8] py-6 text-center">No drift found.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-[#64748B] border-b border-[#E2E8F0] dark:border-slate-700">
          <tr>{cols.map((c) => (<th key={c.k} className={`px-3 py-2 ${c.align || "text-left"}`}>{c.label}</th>))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.material_id || i} className="border-b border-[#F1F5F9] dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
              {cols.map((c) => {
                const val = r[c.k];
                const display = typeof c.fmt === "function" ? c.fmt(val, r) : val;
                return <td key={c.k} className={`px-3 py-2 ${c.align || "text-left"} ${c.cls?.(val, r) || ""}`}>{display}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CheckCard({ check, onFix, fixing }) {
  const [open, setOpen] = useState(check.status !== "ok");
  const Icon = CHECK_ICON[check.key] || ShieldCheck;
  const totals = check.totals || {};
  const canFixAr = check.key === "ar_reconciliation" && check.mismatch_count > 0;
  const canFixReserv = check.key === "reservations_vs_projects" && check.mismatch_count > 0;

  return (
    <div className="card-pb overflow-hidden" data-testid={`integrity-check-${check.key}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center gap-3 border-b border-[#E2E8F0] dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition text-left"
      >
        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${check.status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-[#0A2540] dark:text-white">{check.label}</h3>
            <StatusPill status={check.status} />
            {check.mismatch_count > 0 && (
              <span className="text-xs font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                {check.mismatch_count} drifting
              </span>
            )}
          </div>
          <p className="text-xs text-[#64748B] mt-0.5">{check.description}</p>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-[#94A3B8]" /> : <ChevronRight className="w-5 h-5 text-[#94A3B8]" />}
      </button>

      {open && (
        <div className="p-5 space-y-4">
          {/* Totals */}
          {check.key === "ar_reconciliation" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KV label="Invoices checked" value={check.checked} />
              <KV label="Expected open AR" value={fmtMoney(totals.expected_open_ar || 0)} />
              <KV label="Ledger AR balance" value={fmtMoney(totals.ledger_ar_balance || 0)} />
              <KV label="Delta" value={fmtMoney(totals.delta || 0)} tone={Math.abs(totals.delta || 0) > 0.01 ? "text-red-600" : "text-emerald-700"} />
            </div>
          )}
          {check.key === "wages_payable" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <KV label="Approved timelogs" value={fmtMoney(totals.approved_timelog_total || 0)} />
              <KV label="Ledger 2050 payable" value={fmtMoney(totals.wages_payable_ledger || 0)} />
              <KV label="Delta" value={fmtMoney(totals.delta || 0)} tone={Math.abs(totals.delta || 0) > 0.01 ? "text-red-600" : "text-emerald-700"} />
            </div>
          )}
          {(check.key === "inventory_ledger_vs_stock" || check.key === "reservations_vs_projects") && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <KV label="Materials checked" value={check.checked} />
              <KV label="Drift found" value={check.mismatch_count} tone={check.mismatch_count > 0 ? "text-red-600" : "text-emerald-700"} />
            </div>
          )}

          {/* Fix actions */}
          {(canFixAr || canFixReserv) && (
            <div className="flex flex-wrap gap-2">
              {canFixAr && (
                <button
                  disabled={fixing}
                  onClick={() => onFix("ar-backfill")}
                  data-testid="fix-ar-backfill"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A2540] text-white text-xs font-semibold hover:brightness-110 disabled:opacity-50"
                >
                  <Wrench className="w-3.5 h-3.5" />
                  {fixing ? "Posting…" : `Post missing AR entries for ${check.mismatch_count} invoices`}
                </button>
              )}
              {canFixReserv && (
                <button
                  disabled={fixing}
                  onClick={() => onFix("reservations")}
                  data-testid="fix-reservations"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A2540] text-white text-xs font-semibold hover:brightness-110 disabled:opacity-50"
                >
                  <Wrench className="w-3.5 h-3.5" />
                  {fixing ? "Recomputing…" : "Recompute reserved counters"}
                </button>
              )}
            </div>
          )}

          {/* Body */}
          {check.key === "ar_reconciliation" && <ArTable rows={check.mismatches || []} />}
          {check.key === "inventory_ledger_vs_stock" && (
            <QtyTable
              rows={check.mismatches || []}
              cols={[
                { k: "name",       label: "Material" },
                { k: "sku",        label: "SKU" },
                { k: "stock",      label: "On-hand", align: "text-right" },
                { k: "ledger_qty", label: "Ledger",  align: "text-right" },
                { k: "delta",      label: "Δ",       align: "text-right", cls: (v) => v < 0 ? "text-red-600 font-semibold" : "text-amber-600 font-semibold" },
                { k: "hint",       label: "Hint" },
              ]}
            />
          )}
          {check.key === "reservations_vs_projects" && (
            <QtyTable
              rows={check.mismatches || []}
              cols={[
                { k: "name",         label: "Material" },
                { k: "sku",          label: "SKU" },
                { k: "counter",      label: "SKU counter",   align: "text-right" },
                { k: "projects_sum", label: "Projects sum",  align: "text-right" },
                { k: "delta",        label: "Δ",             align: "text-right", cls: (v) => v !== 0 ? "text-red-600 font-semibold" : "" },
                { k: "hint",         label: "Hint" },
              ]}
            />
          )}
          {check.key === "wages_payable" && check.status !== "ok" && (
            <QtyTable
              rows={check.breakdown || []}
              cols={[
                { k: "employee_name",   label: "Employee" },
                { k: "employee_number", label: "Code" },
                { k: "week_start",      label: "Week of" },
                { k: "hours",           label: "Hours",  align: "text-right" },
                { k: "amount",          label: "Amount", align: "text-right", fmt: (v) => fmtMoney(v || 0) },
              ]}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminDataIntegrity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fixing, setFixing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/data-integrity");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Data Integrity");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runFix = async (kind) => {
    setFixing(true);
    try {
      if (kind === "ar-backfill") {
        const r = await api.post("/admin/data-integrity/fix/ar-backfill", {});
        toast.success(`Posted ${r.data.posted} AR entries (${r.data.skipped} skipped)`);
      } else if (kind === "reservations") {
        const r = await api.post("/admin/data-integrity/fix/reservations", {});
        toast.success(`Recomputed ${r.data.fixed} of ${r.data.checked} materials`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    } finally {
      setFixing(false);
    }
  };

  if (loading) return <div className="text-sm text-[#94A3B8]">Loading Data Integrity…</div>;
  if (!data) return null;

  const s = data.summary || {};
  const allPass = s.failing === 0;

  return (
    <div data-testid="admin-data-integrity" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-[#0A2540] dark:text-white">
              Data Integrity
            </h1>
            {allPass ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5" /> ALL SYSTEMS AGREE
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                <AlertTriangle className="w-3.5 h-3.5" /> {s.failing} CHECK{s.failing === 1 ? "" : "S"} DRIFTING
              </span>
            )}
          </div>
          <p className="text-sm text-[#64748B] dark:text-slate-400 mt-1">
            Single Source of Truth — every operational number is verified against its journal, ledger, or transactional record.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="btn-refresh-integrity"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0A2540] text-white text-sm font-semibold hover:brightness-110 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Re-run checks
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card-pb p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Total checks</div>
          <div className="text-3xl font-heading font-semibold text-[#0A2540] dark:text-white mt-1">{s.total_checks}</div>
        </div>
        <div className="card-pb p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Passing</div>
          <div className="text-3xl font-heading font-semibold text-emerald-600 mt-1">{s.passing}</div>
        </div>
        <div className="card-pb p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Failing</div>
          <div className={`text-3xl font-heading font-semibold mt-1 ${s.failing > 0 ? "text-red-600" : "text-emerald-600"}`}>{s.failing}</div>
        </div>
        <div className="card-pb p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Records drifting</div>
          <div className={`text-3xl font-heading font-semibold mt-1 ${s.mismatches > 0 ? "text-red-600" : "text-emerald-600"}`}>{s.mismatches}</div>
        </div>
      </div>

      {/* Individual checks */}
      <div className="space-y-4">
        {(data.checks || []).map((c) => (
          <CheckCard key={c.key} check={c} onFix={runFix} fixing={fixing} />
        ))}
      </div>
    </div>
  );
}
